import type {
  CallGroup,
  CallGroupPayload,
  CallGroupUser,
  CallGroupUserPayload
} from '@/interfaces/CallGroup.interface';
import BaseApiClient from '../BaseApiClient';
import type { ListDataParams, ListDataResponse } from '../types';
import { mapCallGroupMembers } from './call-group-utils';

export type { CallGroup, CallGroupPayload, CallGroupUser, CallGroupUserPayload };

function unwrapResource<T>(payload: { data?: T } | T): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    payload.data !== undefined
  ) {
    return payload.data as T;
  }
  return payload as T;
}

export class ApiCallGroups extends BaseApiClient {
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<CallGroup>> {
    const params = { ...listData };
    if (searchTerm) {
      if (!params.filters) params.filters = {};
      params.filters.name = searchTerm;
    }

    const res = await this.client.getList('/call-groups', params);
    if (!res.data) {
      throw new Error('Invalid response');
    }

    return {
      items: res.data.data ?? [],
      pagination: {
        page: res.data.meta?.current_page,
        perPage: res.data.meta?.per_page,
        total: res.data.meta?.total
      }
    };
  }

  async get(id: string): Promise<CallGroup> {
    const res = await this.client.get(`/call-groups/${id}`);
    return unwrapResource<CallGroup>(res.data);
  }

  async create(payload: CallGroupPayload): Promise<CallGroup> {
    const res = await this.client.post('/call-groups', payload);
    return unwrapResource<CallGroup>(res.data);
  }

  async update(id: string, payload: CallGroupPayload): Promise<CallGroup> {
    const res = await this.client.put(`/call-groups/${id}`, payload);
    return unwrapResource<CallGroup>(res.data);
  }

  async delete(id: string): Promise<void> {
    await this.client.delete(`/call-groups/${id}`);
  }

  async listUsers(
    callGroupId: string,
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<CallGroupUser>> {
    const group = await this.get(callGroupId);
    const items = mapCallGroupMembers(group, searchTerm);

    return {
      items,
      pagination: {
        page: listData.page ?? 1,
        perPage: items.length || listData.perPage || 15,
        total: items.length
      }
    };
  }

  async addUser(
    callGroupId: string,
    payload: CallGroupUserPayload
  ): Promise<CallGroupUser> {
    const res = await this.client.post(
      `/call-groups/${callGroupId}/users`,
      payload
    );
    return unwrapResource<CallGroupUser>(res.data);
  }

  async removeUser(callGroupId: string, userId: string): Promise<void> {
    await this.client.delete(`/call-groups/${callGroupId}/users/${userId}`);
  }
}

export {
  buildCallGroupPayload,
  callGroupMemberCount,
  callGroupMemberIds,
  callGroupUserDisplayName,
  canDeleteCallGroup,
  mapCallGroupMembers,
  usersForCallGroupPicker
} from './call-group-utils';
export { callGroupsQueryKeys } from './query-keys';
export {
  useCallGroupQuery,
  useCallGroupsListQuery
} from './use-call-groups-queries';
export {
  useAddCallGroupUserMutation,
  useCreateCallGroupMutation,
  useDeleteCallGroupMutation,
  useRemoveCallGroupUserMutation,
  useUpdateCallGroupMutation
} from './use-call-groups-mutations';
